import { useCallback, useEffect, useRef, useState } from 'react';

import {
  alpha,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { useDebouncedCallback } from 'use-debounce';

const DEBOUNCE_DELAY_IN_MILLISECONDS = 1000;

const useStyles = makeStyles(
  ({ palette, spacing, typography, transitions }) => ({
    input: {
      fontSize: typography.pxToRem(14),
      paddingLeft: 0,
      paddingRight: spacing(1),
    },
    inputLabel: {
      fontSize: typography.pxToRem(14),
      color: alpha(palette.text.primary, 0.7),
    },
    iconButton: {
      background: 'none !important',
      color: palette.action.active,
      transform: 'scale(1, 1)',
      transition: transitions.create(['transform', 'color'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
      padding: spacing(0.5),
    },
    iconButtonHidden: {
      transform: 'scale(0, 0)',
      '& > $icon': {
        opacity: 0,
      },
    },
    icon: {
      transition: transitions.create(['opacity'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
      fontSize: 18,
    },
  }),
);

type DebouncedInputProps = {
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  debounceTimer?: number;
  onChange?: (value: string) => void;
  onClear?: () => void;
};

export default function DebouncedInput(props: DebouncedInputProps) {
  const {
    autoFocus = false,
    placeholder = 'Digite...',
    disabled,
    value,
    debounceTimer = DEBOUNCE_DELAY_IN_MILLISECONDS,
    onChange,
    onClear,
  } = props;

  const classes = useStyles();

  const [inputValue, setInputValue] = useState(value || '');
  const previousValueRef = useRef<string>('');

  const debounced = useDebouncedCallback((currentValue: string) => {
    const trimmedValue = currentValue.trim();
    const previousValue = previousValueRef.current.trim();

    if (trimmedValue !== previousValue) {
      onChange?.(trimmedValue);
    }

    previousValueRef.current = currentValue;
  }, debounceTimer);

  const handleClear = useCallback(() => {
    setInputValue('');
    previousValueRef.current = '';
    onClear?.();
  }, [onClear]);

  const handleBlur = useCallback(() => {
    setInputValue(v => v?.trim());
  }, []);

  useEffect(() => {
    setInputValue(value || '');
    previousValueRef.current = value || '';
  }, [value]);

  return (
    <TextField
      variant="outlined"
      size="small"
      autoFocus={autoFocus}
      placeholder={placeholder}
      onBlur={handleBlur}
      value={inputValue}
      onChange={event => {
        const newValue = event.target.value.trimStart();
        setInputValue(newValue);
        debounced(newValue);
      }}
      disabled={disabled}
      InputLabelProps={{
        className: classes.inputLabel,
      }}
      InputProps={{
        className: classes.input,
        endAdornment: inputValue && (
          <InputAdornment position="end">
            <IconButton
              aria-label="Limpar"
              disabled={disabled}
              onClick={handleClear}
              className={classes.iconButton}
              size="small"
            >
              <ClearIcon className={classes.icon} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
